/*
 * Sub-Navigation für Plant-scoped Arbeitsflächen. Zeigt oben in Plant-
 * Seiten (Schema, Discovery, Kandidaten, Dashboard) eine kompakte
 * Tab-Leiste, damit der Techniker klar hat: er arbeitet IN dieser
 * Anlage. Alle Aktionen des Multi-Phase-Discovery-Workflows leben hier.
 *
 * Aktive Tab-Erkennung über useLocation aus preact-iso; jede Tab-URL
 * ist absolut und plant-scoped.
 */

import { useLocation } from 'preact-iso';
import type { JSX } from 'preact';
import { sessionSignal } from '../auth/session';
import { useAsync } from '../hooks/useAsync';
import { api } from '../api/client';
import {
  NavDatapoints, NavAnalysis, NavHmi, NavAlarms, NavDevices, NavUpdate, NavShell,
} from '../icons/HvacIcons';
import './PlantSubNav.css';

interface Props {
  plantId: string;
  /** Optional. Wenn nicht mitgegeben lädt die SubNav den Namen selbst
   *  via GET /api/plants/{id} — damit ruckelt beim Tab-Wechsel nichts
   *  weil jeder Elternteil den Namen mitgibt. Der Platzhalter reserviert
   *  die Höhe während des Fetchs. */
  plantName?: string;
}

interface PlantSummary { id: string; displayName: string }
interface ListPlantsResponse { plants: PlantSummary[] }

interface Tab {
  key: string;
  label: string;
  href: string;
  icon: JSX.Element;
  match: (path: string) => boolean;
  perm?: string;
}

export function PlantSubNav({ plantId, plantName: plantNameProp }: Props) {
  const location = useLocation();
  const perms = sessionSignal.value.user?.permissions ?? [];
  const has = (p: string | undefined) => !p || perms.includes(p);
  // Der Elternteil kann uns den Namen als Prop mitgeben — dann sparen wir
  // den Roundtrip. Wenn er null/undefined/'' liefert (typischer Race:
  // Parent hat noch keine plant-Meta gefetcht), lädt die SubNav selbst.
  // Wichtig: '' ?? fallback ergibt in JS '' — deshalb der explizite
  // Length-Check statt Nullish-Coalescing.
  const parentGaveName = typeof plantNameProp === 'string' && plantNameProp.length > 0;
  // Es gibt keinen GET /api/plants/{id}-Endpoint (nur /schema und /values).
  // Statt einen neuen zu bauen, ziehen wir die ganze Plants-Liste — die
  // ist ohnehin klein und wird von den meisten Seiten (Karte, /plants,
  // Alarme) sowieso schon abgerufen; Browser-HTTP-Cache greift also
  // meistens. Filter nach der aktuellen plantId hier lokal.
  const listState = useAsync(
    (signal) => parentGaveName
      ? Promise.resolve<ListPlantsResponse | null>(null)
      : api.get<ListPlantsResponse>('/api/plants', signal)
          .then((r) => r as ListPlantsResponse | null),
    [parentGaveName],
  );
  const fetchedName = listState.status === 'ok' && listState.data
    ? (listState.data.plants.find((p) => p.id === plantId)?.displayName ?? '')
    : '';
  const resolvedName = parentGaveName ? plantNameProp! : fetchedName;

  const tabs: Tab[] = [
    // "Datenpunkte" ist die Home-Ebene der Plant-Ansicht: Live-Tree +
    // Befehls-Historie erreichen wir beide von hier. Der Commands-Screen
    // matched auch auf diesen Tab damit die Sub-Nav "aktiv" bleibt wenn
    // der Techniker per Toolbar-Button in die Historie wechselt.
    {
      key: 'schema',
      label: 'Datenpunkte',
      href: `/plants/${plantId}`,
      icon: <NavDatapoints size={16} />,
      match: (p) =>
        p === `/plants/${plantId}`
        || p === `/plants/${plantId}/schema`
        || p.startsWith(`/plants/${plantId}/commands`),
    },
    {
      key: 'charts',
      label: 'Auswertung',
      href: `/plants/${plantId}/charts`,
      icon: <NavAnalysis size={16} />,
      match: (p) => p.startsWith(`/plants/${plantId}/charts`),
    },
    {
      key: 'dashboard',
      label: 'Dashboard',
      href: `/dashboard/${plantId}`,
      icon: <NavHmi size={16} />,
      match: (p) => p.startsWith(`/dashboard/${plantId}`),
    },
    // "HMI" ist ab 2026-09-03 die produktive HMI-Route: ein gespeichertes
    // HmiProject gegen die Live-Werte dieser Anlage. Der frueher hier
    // "HMI" genannte Tab zeigte auf /dashboard/:id — das ist die
    // Gridstack-Dashboard-Seite und ein anderes Ding; sie heisst jetzt so,
    // wie sie ist. Der Widget-Katalog gegen den Simulator bleibt unter
    // /hmi/preview und ist bewusst NICHT plant-scoped.
    {
      key: 'hmi',
      label: 'HMI',
      href: `/plants/${plantId}/hmi`,
      icon: <NavHmi size={16} />,
      match: (p) => p.startsWith(`/plants/${plantId}/hmi`),
    },
    {
      key: 'alarms',
      label: 'Alarme',
      href: `/plants/${plantId}/alarms`,
      icon: <NavAlarms size={16} />,
      match: (p) => p.startsWith(`/plants/${plantId}/alarms`),
    },
    // "Devices" fasst die Feld-Ebene zusammen: klassifizierte Geraete +
    // Scan-Kandidaten. Beide Sub-Views teilen sich die Route und schalten
    // intern per Query-Param um. Frueher hiess der Tab "OT" — zu kryptisch
    // fuer Nicht-OT-Muttersprachler.
    {
      key: 'devices',
      label: 'Devices',
      href: `/plants/${plantId}/discovery/candidates`,
      icon: <NavDevices size={16} />,
      match: (p) => p.startsWith(`/plants/${plantId}/discovery/candidates`),
      perm: 'discovery.scan',
    },
    // "Update" zeigt alle Rollout-Sessions, die diese Anlage je gesehen hat,
    // als Master-Detail-Ansicht (Session-Liste + Report-Timeline pro Session).
    // Datenquelle: GET /api/plants/:id/rollouts + /api/rollouts/:id/reports.
    // Zeigt sich fuer alle Rollen — die Endpoints filtern selbst per DataScope.
    {
      key: 'updates',
      label: 'Update',
      href: `/plants/${plantId}/updates`,
      icon: <NavUpdate size={16} />,
      match: (p) => p.startsWith(`/plants/${plantId}/updates`),
    },
    // "Benutzer": wer die Web-HMI dieser Anlage sehen oder bedienen darf
    // (Endkunden, 18.10.2026). Nur mit Benutzerverwaltung.
    {
      key: 'users',
      label: 'Benutzer',
      href: `/plants/${plantId}/users`,
      icon: <NavHmi size={16} />,
      match: (p) => p.startsWith(`/plants/${plantId}/users`),
      perm: 'users.admin',
    },
    // "Shell" ist eine interaktive Remote-Shell gegen einen Field-Client der
    // Anlage. Benoetigt die Permission Permissions.RemoteShell ("remote.shell")
    // — Vergabe ueber die Groups-Verwaltung in /settings/identity. Wire-
    // Kontrakt: docs/wire-remote-shell.md.
    {
      key: 'shell',
      label: 'Shell',
      href: `/plants/${plantId}/shell`,
      icon: <NavShell size={16} />,
      match: (p) => p.startsWith(`/plants/${plantId}/shell`),
      perm: 'remote.shell',
    },
  ];

  return (
    <div class="hv-plant-subnav">
      <div class="hv-plant-subnav__name" title={resolvedName}>
        {resolvedName || ' ' /* nbsp: hält die Höhe während des Fetchs */}
      </div>
      <nav class="hv-plant-subnav__tabs" aria-label="Anlagen-Bereiche">
        {tabs.filter((t) => has(t.perm)).map((t) => (
          <a key={t.key}
             href={t.href}
             class={`hv-plant-subnav__tab${t.match(location.path) ? ' hv-plant-subnav__tab--active' : ''}`}>
            <span class="hv-plant-subnav__tab-icon" aria-hidden="true">{t.icon}</span>
            {t.label}
          </a>
        ))}
      </nav>
    </div>
  );
}
